import { useCallback, useEffect, useState } from 'react';
import {
  callGetAppConfig,
  callUpdateAppConfig,
  type AppConfig,
} from '../firebase';

// Editor del doc Firestore `config/app`.
//
// Hoy solo expone el bloque Android del ForceUpgrade:
//   - minBuild       (versionCode mínimo aceptado, gate duro)
//   - latestBuild    (versionCode publicado más reciente, informativo)
//   - latestVersion  (versionName mostrado en el overlay)
//
// El cliente lee este doc en `useForceUpgrade` y, si el build instalado es
// inferior a `minBuild`, monta `ForceUpgradeOverlay`. Modificar `minBuild` es
// un KILL-SWITCH: subirlo por encima del versionCode actual deja fuera a
// todos los usuarios que no actualicen. Tratar con la misma precaución que
// un despliegue.
//
// El doc puede contener claves que la UI no edita (futuras secciones, iOS,
// feature flags…). La CF mergea — esas claves se conservan intactas.

interface AndroidForm {
  minBuild: string;
  latestBuild: string;
  latestVersion: string;
}

const EMPTY_ANDROID: AndroidForm = { minBuild: '', latestBuild: '', latestVersion: '' };

function androidFromConfig(cfg: AppConfig): AndroidForm {
  const a = cfg.android ?? {};
  return {
    minBuild: a.minBuild != null ? String(a.minBuild) : '',
    latestBuild: a.latestBuild != null ? String(a.latestBuild) : '',
    latestVersion: a.latestVersion ?? '',
  };
}

function parseAndroidForm(form: AndroidForm): { ok: true; value: NonNullable<AppConfig['android']> } | { ok: false; error: string } {
  const minBuildN = Number(form.minBuild);
  const latestBuildN = Number(form.latestBuild);
  if (!Number.isInteger(minBuildN) || minBuildN < 1) {
    return { ok: false, error: 'minBuild debe ser un entero ≥ 1.' };
  }
  if (!Number.isInteger(latestBuildN) || latestBuildN < 1) {
    return { ok: false, error: 'latestBuild debe ser un entero ≥ 1.' };
  }
  if (minBuildN > latestBuildN) {
    return { ok: false, error: 'minBuild no puede ser mayor que latestBuild.' };
  }
  const v = form.latestVersion.trim();
  if (v.length === 0) {
    return { ok: false, error: 'latestVersion no puede estar vacío (ej. "1.0.33").' };
  }
  return { ok: true, value: { minBuild: minBuildN, latestBuild: latestBuildN, latestVersion: v } };
}

export function AppConfigPage() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [android, setAndroid] = useState<AndroidForm>(EMPTY_ANDROID);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setOkMsg(null);
    try {
      const res = await callGetAppConfig({});
      setConfig(res.data.config);
      setAndroid(androidFromConfig(res.data.config));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const baseline = config ? androidFromConfig(config) : EMPTY_ANDROID;
  const dirty =
    android.minBuild !== baseline.minBuild ||
    android.latestBuild !== baseline.latestBuild ||
    android.latestVersion !== baseline.latestVersion;

  async function save() {
    const parsed = parseAndroidForm(android);
    if (!parsed.ok) {
      setErr(parsed.error);
      return;
    }
    setSaving(true);
    setErr(null);
    setOkMsg(null);
    try {
      const res = await callUpdateAppConfig({ android: parsed.value });
      setConfig(res.data.config);
      setAndroid(androidFromConfig(res.data.config));
      setOkMsg('Cambios guardados.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    setAndroid(baseline);
    setErr(null);
    setOkMsg(null);
  }

  return (
    <div>
      <div className="row between" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Configuración de la app</h2>
        <button onClick={reload} disabled={loading || saving}>
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </div>

      <p className="muted" style={{ marginTop: 0 }}>
        Documento Firestore <code>config/app</code>. Lo leen los clientes nativos al arrancar
        para decidir si hay que forzar actualización.
      </p>

      {err && <div className="error">{err}</div>}
      {okMsg && (
        <div
          className="error"
          style={{
            color: 'var(--success)',
            background: 'rgba(34, 197, 94, 0.1)',
            borderColor: 'rgba(34, 197, 94, 0.3)',
          }}
        >
          {okMsg}
        </div>
      )}

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Android · ForceUpgrade</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Subir <strong>minBuild</strong> por encima del <code>versionCode</code> instalado
          bloquea la app de los usuarios afectados hasta que actualicen desde Play Store.
          Tratar como un despliegue.
        </p>

        <div className="config-form">
          <label>
            <span className="config-label">minBuild</span>
            <span className="muted config-hint">
              versionCode mínimo aceptado (gate duro). Builds inferiores ven el overlay no-cerrable.
            </span>
            <input
              type="number"
              min={1}
              step={1}
              value={android.minBuild}
              onChange={(e) => setAndroid((p) => ({ ...p, minBuild: e.target.value }))}
              disabled={loading || saving}
            />
          </label>

          <label>
            <span className="config-label">latestBuild</span>
            <span className="muted config-hint">
              Último versionCode publicado. Informativo (no bloquea).
            </span>
            <input
              type="number"
              min={1}
              step={1}
              value={android.latestBuild}
              onChange={(e) => setAndroid((p) => ({ ...p, latestBuild: e.target.value }))}
              disabled={loading || saving}
            />
          </label>

          <label>
            <span className="config-label">latestVersion</span>
            <span className="muted config-hint">
              versionName mostrado al usuario en el overlay (ej. <code>1.0.33</code>).
            </span>
            <input
              type="text"
              value={android.latestVersion}
              onChange={(e) => setAndroid((p) => ({ ...p, latestVersion: e.target.value }))}
              disabled={loading || saving}
              placeholder="1.0.33"
            />
          </label>
        </div>

        <div className="row" style={{ marginTop: 16, gap: 8 }}>
          <button className="primary" onClick={save} disabled={!dirty || saving || loading}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
          <button onClick={discard} disabled={!dirty || saving || loading}>
            Descartar
          </button>
          {dirty && <span className="muted">· Cambios sin guardar</span>}
        </div>
      </div>

      {config && (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>Doc actual</h3>
          <pre
            style={{
              margin: 0,
              padding: 12,
              background: 'var(--panel-2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              fontSize: 12,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(config, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
